import { useEffect } from 'react';

import { Navigation } from '@quizzach/components';
import { Reveal } from '@quizzach/motion';
import { LegalHead, LegalPage, PrivacyArticle } from '@quizzach/styles';
import { scrollToTop } from '@quizzach/utils';

/*
 * Croatian-only legal document; content mirrors the former static page
 * (app/public/privacy-policy.html), so it stays out of the i18n catalogs.
 */
export const PrivacyPolicy = () => {
	useEffect(() => {
		scrollToTop();
	}, []);

	return (
		<LegalPage>
			<Navigation />

			<LegalHead>
				<Reveal as='h1'>Informacije o obradi osobnih podataka</Reveal>
				<Reveal delay={1}>
					<span className='hand'>Datum zadnjeg ažuriranja: 22.10.2026.</span>
				</Reveal>
			</LegalHead>

			<PrivacyArticle data-id='privacy-policy-container'>
				{/* plain div: Reveal's 12% threshold never fires on a column this tall */}
				<div className='col'>
					<p>
						Ovim dokumentom trgovačko društvo Incendio d.o.o. daje informacije o
						obradi osobnih podataka posjetitelja i registriranih korisnika web
						stranice Quizzach.com, korisnika televizijske aplikacije Quizzach,
						naših kupaca te poslovnih partnera (npr. autora kvizova). Osobne
						podatke prikupljamo i obrađujemo u skladu s Općom uredbom o zaštiti
						podataka i drugim važećim propisima.
					</p>

					<h2>1. Tko je voditelj obrade?</h2>
					<p>
						Voditelj obrade je Incendio d.o.o., OIB 19946433374, Vladimira
						Nazora 192, Podgajci Podravski, e-pošta: team@quizzach.com.
					</p>

					<h2>2. Koje osobne podatke obrađujemo?</h2>
					<ul>
						<li>
							ime i prezime, OIB i adresu autora kvizova i poslovnih partnera,
							odnosno njihovih zakonskih zastupnika
						</li>
						<li>
							ime i prezime, adresu e-pošte i korisničko ime registriranih
							korisnika
						</li>
						<li>
							podatke o transakcijama i plaćanjima (datum i iznos transakcije,
							IBAN i sl.)
						</li>
						<li>podatke o kupnjama i pregledavanjima</li>
						<li>ocjene kvalitete i ocjene pitanja</li>
						<li>
							IP adresu i osnovne tehničke podatke o pristupu (vrijeme pristupa,
							vrsta uređaja i preglednika), koji se bilježe u tehničkim zapisima
						</li>
						<li>podatke prikupljene kolačićima (vidi točku 7.)</li>
					</ul>

					<h2>3. U koje svrhe i po kojim pravnim osnovama?</h2>
					<ol type='a'>
						<li>
							<strong>Sklapanje i izvršenje ugovora</strong>: registracija i
							vođenje korisničkog računa (ime i prezime, adresa e-pošte,
							korisničko ime; adresa e-pošte služi za potvrdu registracije,
							ponovno postavljanje lozinke i komunikaciju o računu), kupoprodaja
							kvizova te ugovori s autorima i poslovnim partnerima, uključujući
							provedbu plaćanja. Bez podataka nužnih za ugovor nismo ga u
							mogućnosti sklopiti niti izvršiti.
						</li>
						<li>
							<strong>Legitimni interes</strong>: preporuke kvizova na temelju
							Vaših prethodnih kupnji, analiza uspješnosti i kvalitete kvizova i
							autora (uključujući ocjene) te zaštita i sigurnost sustava i
							otklanjanje tehničkih pogrešaka (IP adresa i tehnički zapisi;
							ograničenje broja zahtjeva, sprječavanje zlouporabe). Takvoj
							obradi možete u svakom trenutku prigovoriti (vidi točku 9.); u tom
							slučaju prestajemo s obradom, osim ako dokažemo da naši uvjerljivi
							legitimni razlozi nadilaze Vaša prava i interese. Prigovorite li
							obradi u svrhe izravnog marketinga, uključujući s njime povezanu
							izradu profila, s obradom prestajemo bezuvjetno.
						</li>
						<li>
							<strong>Ispunjenje zakonskih obveza</strong> (npr. računovodstveni
							i porezni propisi, postupanje po Vašim prigovorima).
						</li>
						<li>
							<strong>Privola</strong>: analitički i marketinški kolačići (vidi
							točku 7.). Privolu možete povući u svakom trenutku, bez utjecaja
							na zakonitost obrade prije povlačenja.
						</li>
					</ol>

					<h2>4. Od kuda prikupljamo podatke?</h2>
					<p>
						Izravno od Vas. Prijavite li se putem svog Google računa, ime i
						prezime te adresu e-pošte zaprimamo od društva Google uz Vaše
						odobrenje. Za kupnje plaćene putem Paddlea od društva Paddle
						zaprimamo podatke o transakciji potrebne za isporuku i evidenciju
						kupnje (npr. broj transakcije, datum i iznos).
					</p>

					<h2>5. Tko ima pristup podacima?</h2>
					<p>Zaposlenici voditelja obrade, a prema potrebi i:</p>
					<ul>
						<li>
							institucije za platni promet te Paddle.com Market Limited, naš
							ovlašteni online preprodavatelj (Merchant of Record), koji naplatu
							kupnji provodi kao zasebni voditelj obrade u skladu sa svojim
							informacijama o privatnosti (www.paddle.com/legal/privacy)
						</li>
						<li>knjigovodstveni servis</li>
						<li>
							pružatelji IT usluga: poslužitelji i održavanje sustava, mrežna
							isporuka i zaštita stranice (Cloudflare), slanje e-pošte (Zoho),
							pretraživanje sadržaja (Algolia), praćenje tehničkih pogrešaka
							(Sentry)
						</li>
						<li>
							pružatelji analitike i mjerenja posjećenosti, isključivo uz Vašu
							privolu (Google, Meta, Microsoft; vidi točku 7.)
						</li>
						<li>
							nadležna državna tijela u skladu sa zakonom (npr. Porezna uprava)
						</li>
						<li>
							odvjetnici, sudovi i druge osobe radi postavljanja, ostvarivanja
							ili obrane pravnih zahtjeva
						</li>
					</ul>

					<h2>6. Gdje se podaci obrađuju?</h2>
					<p>
						U pravilu na teritoriju Europskog gospodarskog prostora. Paddle.com
						Market Limited ima sjedište u Ujedinjenoj Kraljevini, za koju je
						Europska komisija donijela odluku o primjerenosti. Google, Meta,
						Microsoft, Cloudflare i Sentry dio podataka mogu obrađivati u SAD-u
						temeljem odluke Europske komisije o primjerenosti za Okvir za
						privatnost podataka EU-SAD (EU-U.S. Data Privacy Framework); na
						ostale prijenose izvan Europskog gospodarskog prostora primjenjuju
						se standardne ugovorne klauzule Europske komisije, čiju presliku
						možete zatražiti putem kontakta iz točke 12.
					</p>

					<h2>7. Kolačići i slične tehnologije</h2>
					<p>
						Nužni kolačići i pohrana (zapis Vašeg odabira o kolačićima,
						pristupni token prijavljenog korisnika, odabir izgleda sučelja,
						privremeni tijek kviza) potrebni su za rad stranice i za njih se
						privola ne traži. Analitičke i marketinške alate, Google Analytics
						(Google), Meta Pixel (Meta) i Microsoft Clarity (Microsoft),
						koristimo isključivo uz Vašu prethodnu privolu danu putem obavijesti
						o kolačićima. Privolu možete povući brisanjem kolačića u postavkama
						preglednika, nakon čega se obavijest ponovno prikazuje. Za prijenos
						izvan Europskog gospodarskog prostora vidi točku 6.
					</p>

					<h2>8. Izrada profila</h2>
					<p>
						Kupcima na temelju prethodnih kupnji dajemo personalizirane
						preporuke kvizova. Ne donosimo odluke koje bi se temeljile
						isključivo na automatiziranoj obradi i proizvodile pravne učinke za
						Vas.
					</p>

					<h2>9. Koja su Vaša prava?</h2>
					<p>
						Imate pravo na pristup svojim osobnim podacima i informacijama o
						njihovoj obradi, na ispravak, brisanje, ograničenje obrade,
						prenosivost podataka, prigovor na obradu temeljenu na legitimnom
						interesu (uključujući izravni marketing i s njime povezanu izradu
						profila) te na povlačenje privole u svakom trenutku. Prava
						ostvarujete na način iz točke 12.; radi sprječavanja neovlaštenog
						otkrivanja podataka prije postupanja moramo provjeriti Vaš
						identitet.
					</p>

					<h2>10. Prigovor nadzornom tijelu</h2>
					<p>
						Prigovor možete podnijeti Agenciji za zaštitu osobnih podataka
						(AZOP), Ulica grada Vukovara 54, Zagreb, 00385 (0)1 4609 000,
						azop@azop.hr, www.azop.hr, ili drugom nadležnom europskom nadzornom
						tijelu.
					</p>

					<h2>11. Koliko dugo čuvamo podatke?</h2>
					<p>
						Onoliko dugo koliko je potrebno za svrhu obrade: za trajanja
						korisničkog odnosa ili poslovne suradnje; koliko nalažu zakonske
						obveze (npr. čuvanje računovodstvene dokumentacije); do isteka
						zastarnih rokova radi postavljanja, ostvarivanja ili obrane pravnih
						zahtjeva; odnosno dok postoji legitimni interes (npr. preporuke
						prema povijesti kupnji).
					</p>

					<h2>12. Kako nas možete kontaktirati?</h2>
					<p>
						Za ostvarivanje prava i sve upite o obradi osobnih podataka: pisanim
						putem na adresu Incendio d.o.o., Vladimira Nazora 192, Podgajci
						Podravski, ili e-poštom na team@quizzach.com.
					</p>

					<h2>13. Obrada podataka u televizijskoj aplikaciji Quizzach</h2>
					<p>
						Quizzach TV aplikacija (dostupna putem službenih trgovina
						aplikacija, npr. Google Play za Android TV) služi pokretanju kvizova
						na televizoru unosom koda za igru. Ne zahtijeva registraciju,
						prijavu ni unos osobnih podataka te ne koristi kolačiće, analitiku,
						oglašavanje ni programske komponente trećih strana. Obrađuju se
						samo:
					</p>
					<ul>
						<li>
							<strong>ocjene pitanja</strong> (težina, sviđanje): pohranjuju se
							isključivo uz oznaku pitanja, bez identifikatora osobe ili
							uređaja; koristimo ih, kao i ostale ocjene, temeljem legitimnog
							interesa za poboljšanje kvalitete kvizova (vidi točku 3.)
						</li>
						<li>
							<strong>IP adresa uređaja</strong>: pri komunikaciji s našim
							poslužiteljem (isključivo šifriranom HTTPS vezom) privremeno se
							bilježi u tehničkim zapisima temeljem legitimnog interesa zaštite
							i sigurnosti sustava te se ne povezuje s pojedinim korisnikom
						</li>
					</ul>
					<p>
						Na mjesto obrade primjenjuje se točka 6., a na odgovarajući način i
						točke 9.-12. Budući da ocjene ne sadrže identifikatore pošiljatelja,
						zahtjeve koji pretpostavljaju njegovu identifikaciju (npr. brisanje
						pojedinačne ocjene) u pravilu ne možemo izvršiti (članak 11. Opće
						uredbe o zaštiti podataka).
					</p>

					<h2>14. Ažuriranje ovih informacija</h2>
					<p>
						Informacije redovito ažuriramo; najnovija verzija uvijek je dostupna
						na našim stranicama, a o važnijim promjenama obavijestit ćemo Vas
						izravno. Ove informacije važeće su od 22.10.2026.
					</p>
				</div>
			</PrivacyArticle>
		</LegalPage>
	);
};
